import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroup as SurveyGroupBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type SurveyGroup = App.PickGraphQL<SurveyGroupBase, 'id' | 'title'>

export type SurveyGroupSelectorProps<TSurveyGroup extends SurveyGroup> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  surveyGroups: TSurveyGroup[]
  nullText?: string
  onChange?: (surveyGroupId: TSurveyGroup['id']) => void
  unsetText?: string
  value: TSurveyGroup['id'] | Unassigned | null
}

export const SurveyGroupSelector = React.forwardRef(
  <TSurveyGroup extends SurveyGroup>(
    {
      nullText,
      onChange,
      surveyGroups,
      unsetText,
      value: valueProp,
      ...rest
    }: SurveyGroupSelectorProps<TSurveyGroup>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () =>
        surveyGroups.find(({ id }) => id === valueProp) ??
        (valueProp === null
          ? { id: null, name: nullText }
          : { id: unassignedSymbol, name: unsetText }),
      [surveyGroups, nullText, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (surveyGroup: TSurveyGroup) => {
        onChange?.(surveyGroup.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={surveyGroups}
        dataItemKey="id"
        textField="title"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
